import React, { memo } from 'react'
import styles from './CreateBannerSideDialog.module.scss'
import CommonSideDialog from '../../../../common/components/side-dialog/CommonSideDialog';
import { useForm } from 'react-hook-form';
import CommonFormFieldWrapper, { getFormInputErrors } from '../../../../common/components/form-field-wrapper/CommonFormFieldWrapper';
import CommonFileUploadInput, { StyleOfFileInput } from '../../../../common/components/file-upload-input/CommonFileUploadInput';
import { useCreateAdvertBannerMutation } from '../../../../service/advert-banner-service';
import { toast } from 'react-toastify';

type Props = {
    onCloseDialog: () => void;
    onSuccess: () => void;
    setLoading: (status: boolean) => void;
}

type CreateAdvertBannerForm = {
    name: string;
    picture?: File;
    redirectUrl: string;
}

const CreateBannerSideDialog: React.FC<Props> = memo((props: Props) => {
    const { onCloseDialog, onSuccess, setLoading } = props;

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors, isValid, isSubmitted } 
    } = useForm<CreateAdvertBannerForm>({
        reValidateMode: 'onChange',
        mode: 'onBlur',
        defaultValues: {
            name: '',
            redirectUrl: ''
        }
    });

    const onFilesSelection = (files: File[]) => {
        if (files.length > 0) {
            setValue('picture', files[0], {
                shouldValidate: true,
                shouldTouch: true,
                shouldDirty: true,
            });
        }
    }

    const [createAdvertBanner, { isLoading, error }] = useCreateAdvertBannerMutation();

    const onSubmit = async (data: CreateAdvertBannerForm) => {
        const formData = new FormData();
        formData.append('name', data.name);
        if (data.picture) {
            formData.append('picture', data.picture);
        }
        formData.append('redirectUrl', data.redirectUrl);
    
        setLoading(true);
        try {
            await createAdvertBanner(formData).unwrap();
            onSuccess();
            toast.success('Advert Banner successfully created');
        } catch (e: any) {
            if (e?.data?.title) {
                toast.error(e.data.title);
            } else {
                toast.error('Failed to create advert banner');
            }
        } finally {
            onCloseDialog();
            setLoading(false);
        }
    };

    return (
        <CommonSideDialog onCloseDialog={onCloseDialog}>
            <form onSubmit={handleSubmit(onSubmit)} className={styles.container}>
                <div className={styles.header}>
                    Create Banner
                    <div className={styles.controls}>
                        <button disabled={isSubmitted && !isValid} className={`standardButton ${styles.btn}`}>Create Banner</button>
                        <svg className={styles.clickable} onClick={props.onCloseDialog} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM9.35355 8.64645C9.15829 8.45118 8.84171 8.45118 8.64645 8.64645C8.45118 8.84171 8.45118 9.15829 8.64645 9.35355L11.2929 12L8.64645 14.6464C8.45118 14.8417 8.45118 15.1583 8.64645 15.3536C8.84171 15.5488 9.15829 15.5488 9.35355 15.3536L12 12.7071L14.6464 15.3536C14.8417 15.5488 15.1583 15.5488 15.3536 15.3536C15.5488 15.1583 15.5488 14.8417 15.3536 14.6464L12.7071 12L15.3536 9.35355C15.5488 9.15829 15.5488 8.84171 15.3536 8.64645C15.1583 8.45118 14.8417 8.45118 14.6464 8.64645L12 11.2929L9.35355 8.64645Z" fill="black"/>
                        </svg>
                    </div>
                </div>
                <div className={styles.content}>
                    <CommonFormFieldWrapper title="Banner Name" required errors={getFormInputErrors(errors.name?.message)}>
                        <input {...register("name", { required: "Advert Banner Name is required" })} placeholder="Enter banner name" type="text" />
                    </CommonFormFieldWrapper>
                    <CommonFormFieldWrapper title="Banner image" required errors={getFormInputErrors(errors.picture?.message)}>
                        <CommonFileUploadInput
                            maxFileSize={24}
                            accept='image/*'
                            styleOfInput={StyleOfFileInput.FormInput}
                            registerInputProps={register("picture", { required: "Banner image is required" })}
                            onFilesSelect={onFilesSelection}
                        />
                    </CommonFormFieldWrapper>
                    <CommonFormFieldWrapper title="Redirect Url" required errors={getFormInputErrors(errors.redirectUrl?.message)}>
                        <input {...register("redirectUrl", { 
                                    required: "Redirect Url is required",
                                    pattern: {
                                        value: /^(https?:\/\/).+$/,
                                        message: "Redirect Url must be a valid URL starting with http:// or https://"
                                    }
                                })} 
                                placeholder="Enter URL" 
                                type="text" 
                        />
                    </CommonFormFieldWrapper>
                </div>
            </form>
        </CommonSideDialog>
    )
})

export default CreateBannerSideDialog